import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import './AuthScreen.css';

const SignUpScreen: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const { signUp, signInWithGoogle } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const result = await signUp(formData.email, formData.password, formData.name);
    
    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } else {
      setError(result.error || 'Failed to create account');
    }
    
    setLoading(false);
  };

  if (success) {
    return (
      <div className="auth-container" style={{ backgroundColor: theme.colors.background }}>
        <div className="auth-card" style={{ backgroundColor: theme.colors.surface }}>
          <div className="success-message">
            <h2 style={{ color: 'var(--shared-primary)' }}>🎉 Welcome to My Sports Coach!</h2>
            <p>Your account has been created successfully.</p>
            <p>Redirecting to your dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container" style={{ backgroundColor: theme.colors.background }}>
      <div className="auth-card" style={{ backgroundColor: theme.colors.surface }}>
        <div className="auth-header">
          <h1 style={{ color: 'var(--shared-primary)' }}>Create Account</h1>
          <p>Join My Sports Coach and start improving your game!</p>
        </div>

        {error && (
          <div className="error-message" style={{ backgroundColor: theme.colors.error + '20', color: theme.colors.error }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              style={{ 
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              style={{ 
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              minLength={6}
              style={{ 
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <input
              type="password"
              id="confirmPassword"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              style={{ 
                borderColor: theme.colors.border,
                backgroundColor: theme.colors.background,
                color: theme.colors.text
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button"
            style={{ 
              backgroundColor: 'var(--shared-primary)',
              color: theme.colors.surface
            }}
          >
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>

          <div className="auth-divider">or</div>
          <button
            type="button"
            disabled={loading}
            className="auth-button auth-button-google"
            onClick={async () => {
              setError('');
              setLoading(true);
              const result = await signInWithGoogle();
              if (result.success) navigate('/');
              else setError(result.error || 'Google sign-in failed');
              setLoading(false);
            }}
          >
            Sign up with Google
          </button>
        </form>

        <div className="auth-footer">
          <p>
            Already have an account?{' '}
            <Link to="/signin" style={{ color: 'var(--shared-primary)' }}>
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignUpScreen;